import React, { useRef } from 'react'
import { Handle, Position } from '@xyflow/react'
import { DismissibleTooltip } from '../../../../common/DismissibleTooltip'
import { SparkleLoader } from '../../../../common/SparkleLoader'
import { PulsingHandleButton } from '../styles/BaseNodeStyles'
import { CollapsedSourceHandle } from '../styles/CollapsedStyles'
import {
  HandleWrapper,
  HandleLine,
  HandleButtonIcon,
  InvisibleTargetHandle,
  SourceHandle,
  FloatingAIBadge,
  FloatingAIBadgeCount,
} from '../../styles/HandleStyles'
import type { NodeCategory } from '@bandos/shared'

interface NodeHandlesProps {
  hideSourceHandle: boolean
  hideVisualHandle?: boolean // When true, only renders invisible target + left handle (dock takes over visual)
  isReadOnly: boolean
  childNodeInfo: {
    icon: React.ComponentType<{ size: number; color: string; strokeWidth?: number }>
    color: string
    tooltip: string
  }
  shouldPulse: boolean
  isNodeGenerating: boolean
  hasSuggestions: boolean
  suggestionCount: number
  nodeId: string
  nodeCategory: NodeCategory
  onSourceHandleClick?: () => void
  handleAddChild: (nodeId: string, category: NodeCategory) => void
  renderLeftHandle?: () => React.ReactNode
}

export const NodeHandles = React.memo(
  ({
    hideSourceHandle,
    hideVisualHandle,
    isReadOnly,
    childNodeInfo,
    shouldPulse,
    isNodeGenerating,
    hasSuggestions,
    suggestionCount,
    nodeId,
    nodeCategory,
    onSourceHandleClick,
    handleAddChild,
    renderLeftHandle,
  }: NodeHandlesProps) => {
    const handleWrapperRef = useRef<HTMLDivElement>(null)
    const ChildNodeIcon = childNodeInfo.icon

    return (
      <>
        {/* Invisible target handle on left for receiving connections */}
        {/* isConnectableStart={false} prevents dragging FROM this handle while still allowing connections TO it */}
        <InvisibleTargetHandle
          type="target"
          position={Position.Left}
          id="target"
          isConnectableStart={false}
        />

        {/* Optional left handle for adding parent nodes */}
        {renderLeftHandle && renderLeftHandle()}

        {/* Visual handle button — hidden when dock takes over */}
        {!hideSourceHandle && !isReadOnly && !hideVisualHandle && (
          <DismissibleTooltip title={childNodeInfo.tooltip} mouseEnterDelay={0.5} placement="right">
            <HandleWrapper
              ref={handleWrapperRef}
              className="nodrag"
              onClick={(e) => {
                e.stopPropagation()
                if (onSourceHandleClick) {
                  onSourceHandleClick()
                } else {
                  handleAddChild(nodeId, nodeCategory)
                }
              }}
            >
              <HandleLine $accentColor={childNodeInfo.color} $direction="right" />
              <PulsingHandleButton $accentColor={childNodeInfo.color} $shouldPulse={shouldPulse}>
                <HandleButtonIcon>
                  {isNodeGenerating ? (
                    <SparkleLoader isAnimating={true} size={24} />
                  ) : (
                    <ChildNodeIcon size={14} color={childNodeInfo.color} strokeWidth={2.5} />
                  )}
                </HandleButtonIcon>
                <SourceHandle type="source" position={Position.Right} id="right-source" />
                {/* Floating AI badge when suggestions are available */}
                {hasSuggestions && !isNodeGenerating && suggestionCount > 0 && (
                  <FloatingAIBadge>
                    <SparkleLoader isAnimating={false} size={28} />
                    <FloatingAIBadgeCount>{suggestionCount}</FloatingAIBadgeCount>
                  </FloatingAIBadge>
                )}
              </PulsingHandleButton>
            </HandleWrapper>
          </DismissibleTooltip>
        )}

        {/* Invisible source handle — always present for edge routing when dock is active */}
        {/* Uses plain Handle (no style overrides) so React Flow positions it at center-right */}
        {!hideSourceHandle && hideVisualHandle && (
          <Handle
            type="source"
            position={Position.Right}
            id="right-source"
            style={{ opacity: 0, pointerEvents: 'none' }}
          />
        )}

        {/* In read-only mode, render just the source handle without the interactive button */}
        {!hideSourceHandle && !hideVisualHandle && isReadOnly && (
          <CollapsedSourceHandle type="source" position={Position.Right} id="right-source" />
        )}
      </>
    )
  }
)
